import { NgTemplateOutlet, NgClass } from '@angular/common';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { CdkStep, CdkStepHeader, CdkStepper } from '@angular/cdk/stepper';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    HostListener,
    ElementRef,
    forwardRef,
    OnInit,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
    input,
    inject
} from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';

import { ApButtonComponent } from '@ap/button';
import { ApContentService, ApContentModule } from '@ap/content';

import { ApStepControlsDirective } from './step-controls/step-controls.directive';
import { apStepperAnimations } from './stepper-animations';
import { ApStepperHeaderComponent } from './stepper-header/stepper-header.component';
import content from './stepper.content.json';

@Component({
    selector: 'ap-step',
    template: '<ng-template><ng-content></ng-content></ng-template>',
    encapsulation: ViewEncapsulation.None,
    exportAs: 'matStep',
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApStepComponent extends CdkStep {
    /**
     * Content for step label given by `<ng-template apStepLabel>`.
     * @internal
     */
    @ContentChild(ApStepControlsDirective, { static: true })
    public apStepControls: ApStepControlsDirective;

    /**
     * Is this a sub step?
     * Defaults to false
     */

    public readonly isSubStep = input(false);

    /**
     * Step number
     */
    public readonly stepNumber = input<number>(null);

    public constructor() {
        const stepper = inject(forwardRef(() => ApStepperDirective));
        super(stepper);
    }

    /** Custom error state matcher that additionally checks for validity of interacted form. */
    protected isErrorState(control: FormControl<string> | null, form: FormGroupDirective | NgForm | null): boolean {
        const originalErrorState = this.provideErrorState(control, form);

        // Custom error state checks for the validity of form that is not submitted or touched
        // since user can trigger a form change by calling for another step without directly
        // interacting with the current form.
        const customErrorState = !!(control && control.invalid && this.interacted);

        return originalErrorState || customErrorState;
    }

    private readonly provideErrorState = (control: FormControl<string> | null, form: FormGroupDirective | NgForm | null): boolean =>
        !!(control && control.invalid && (control.touched || (form && form.submitted)));
}

@Directive({
    selector: '[apStepper]'
})
export class ApStepperDirective extends CdkStepper implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly liveAnnouncer = inject(LiveAnnouncer);

    /** The list of step headers of the steps in the stepper. */
    @ViewChildren(ApStepperHeaderComponent, { read: ElementRef })
    protected stepHeader: QueryList<CdkStepHeader>;

    /**
     * Steps that the stepper holds
     * @internal
     */
    @ContentChildren(ApStepComponent, { descendants: true })
    declare _steps: QueryList<ApStepComponent>;

    public uniqueId: string;

    constructor() {
        const dir = inject(Directionality, { optional: true });
        const changeDetectorRef = inject(ChangeDetectorRef);
        const elementRef = inject(ElementRef<HTMLElement>);
        super(dir, changeDetectorRef, elementRef);
        this.stepHeader = this._stepHeader;

        // default linear value.
        this.linear = true;
        this.uniqueId = Math.random().toString(36).substring(2, 15);
    }

    protected get _mainSteps(): ApStepComponent[] {
        return this._steps.filter((step: ApStepComponent) => !step.isSubStep());
    }

    protected _getSelectedIndex(): number {
        const stepNumber = this._steps.toArray()[this.selectedIndex].stepNumber();
        return stepNumber ? stepNumber - 1 : this.selectedIndex;
    }

    protected _hasStepperControls(currentStepIndex: number): boolean {
        const arrayOfSteps = this._steps.toArray();

        return !(!!arrayOfSteps[currentStepIndex].apStepControls && this.selectedIndex === currentStepIndex);
    }

    protected _showSummary(index: number): boolean {
        const arrayOfSteps = this._steps.toArray();
        const stepControl = arrayOfSteps[index].stepControl;

        if (!stepControl) {
            return true;
        }

        if (this.linear) {
            return index !== this.selectedIndex && arrayOfSteps[index].completed;
        }

        return index !== this.selectedIndex && stepControl.touched && stepControl.value;
    }

    /** gets the progress of the current subStep from to the total subSteps in the current step. returns progress in ceiled percentage */
    protected getStepProgress(): number {
        const stepArray = this._steps.toArray();
        const currentStep = stepArray[this.selectedIndex];

        if (currentStep.stepNumber() == null) {
            return 0;
        }

        const subSteps = stepArray.filter((d) => d.stepNumber() === currentStep.stepNumber());
        const currentSubStep = subSteps.indexOf(currentStep);
        const totalSubSteps = subSteps.length;
        return Math.ceil((currentSubStep / totalSubSteps) * 100);
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
    }

    /**
     * Needs to be public to match CDKStepper interface
     * @internal
     */
    public next(): void {
        super.next();
        this.announceStep();
    }

    /**
     * Needs to be public to match CDKStepper interface
     * @internal
     */
    public previous(): void {
        super.previous();
        this.announceStep();
    }

    /**
     * Needs to be public to match CDKStepper interface
     * @internal
     */
    public reset(): void {
        super.reset();
        this.announceStep();
    }

    private announceStep(): void {
        const stepArray = this._steps.toArray();
        const currentStep = stepArray[this.selectedIndex];

        const announcement = this.getAnnouncementText(stepArray, currentStep);
        this.liveAnnouncer.announce(announcement);
    }

    private getAnnouncementText(stepArray: ApStepComponent[], currentStep: ApStepComponent): string {
        const subSteps = stepArray.filter((d) => d.stepNumber() === currentStep.stepNumber());
        const currentSubStep = subSteps.indexOf(currentStep);
        const totalSubSteps = subSteps.length;

        return currentSubStep > 0 && totalSubSteps > 1
            ? this.contentService.get('stepper.announcer.substep-text', {
                  title: currentStep.label || '',
                  currentStep: (this._getSelectedIndex() + 1).toString(),
                  totalSteps: this._mainSteps.length.toString(),
                  currentSubstep: currentSubStep.toString(),
                  totalSubsteps: (totalSubSteps - 1).toString()
              })
            : this.contentService.get('stepper.announcer.text', {
                  title: currentStep.label || '',
                  currentStep: (this._getSelectedIndex() + 1).toString(),
                  totalSteps: this._mainSteps.length.toString()
              });
    }
}

@Component({
    selector: 'ap-stepper',
    templateUrl: 'stepper.component.html',
    animations: [apStepperAnimations.stepTransition],
    providers: [
        { provide: ApStepperDirective, useExisting: ApStepperComponent },
        { provide: CdkStepper, useExisting: ApStepperComponent }
    ],
    encapsulation: ViewEncapsulation.None,
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ApStepperHeaderComponent,
        NgTemplateOutlet,
        ApContentModule,
        NgClass,
        ApButtonComponent,
        forwardRef(() => ApStepperPreviousDirective),
        forwardRef(() => ApStepperNextDirective)
    ]
})
export class ApStepperComponent extends ApStepperDirective implements OnInit {
    protected hostClass = false;

    public ngOnInit(): void {
        this.linear = true;
        super.ngOnInit();
        this.hostClass = true;
    }
}

/**
 * Button that moves to the next step in a stepper workflow.
 * Implementation from https://github.com/angular/components/blob/master/src/cdk/stepper/stepper-button.ts
 */
@Directive({
    selector: 'button[apStepperNext], ap-button[apStepperNext]',
    providers: [{ provide: CdkStepper, useExisting: ApStepperDirective }]
})
export class ApStepperNextDirective {
    private readonly _stepper = inject(CdkStepper); // eslint-disable-line @typescript-eslint/naming-convention

    @HostListener('click')
    public onclick(): void {
        this._stepper.next();
    }
}

/**
 * Button that moves to the previous step in a stepper workflow.
 */
@Directive({
    selector: 'button[apStepperPrevious], ap-button[apStepperPrevious]',
    providers: [{ provide: CdkStepper, useExisting: ApStepperDirective }]
})
export class ApStepperPreviousDirective {
    private readonly _stepper = inject(CdkStepper); // eslint-disable-line @typescript-eslint/naming-convention

    @HostListener('click')
    public onclick(): void {
        this._stepper.previous();
    }
}
